import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";
import { getDocLinkConsultStatusLabel } from "@/config/docLinkConsultStatus";

const PURPLE = DOCLINK_COLORS.DARK_PURPLE;
const ORANGE = DOCLINK_COLORS.ORANGE;
const PERTH_TZ = "Australia/Perth";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: PERTH_TZ,
      day: "2-digit",
      month: "2-digit",
    }).format(new Date(iso));
  } catch {
    return String(iso);
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: PERTH_TZ,
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(iso));
  } catch {
    return String(iso);
  }
}

/**
 * Format a datetime string that is already in Perth local time (no UTC conversion).
 * The CONSULTSTATUSLOG table stores datetimes as Perth local time without a timezone.
 * We strip any trailing timezone info (Z, +HH:MM, .000Z) before parsing so that the
 * raw digits stored in the DB are always displayed — regardless of how the MySQL driver
 * serialises the value.
 */
function fmtLocalDateTime(str) {
  if (!str) return "—";
  // Normalise: replace T separator, then strip timezone suffix (Z / +xx:xx / .000Z etc.)
  const clean = String(str)
    .replace("T", " ")
    .replace(/([+-]\d{2}:\d{2}|Z|\.000Z|\.\d+Z?)$/i, "")
    .trim();
  const [datePart = "", timePart = ""] = clean.split(" ");
  const [, m, d] = datePart.split("-");
  const [hh = "0", mm = "00"] = timePart.split(":");
  const h = Number(hh);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return `${d}/${m} ${h12}:${mm}${ampm}`;
}

// Keys match C# ConsultStatus enum [Description] values exactly
const STATUS_COLORS = {
  "None":                    "#94a3b8",
  "Request sent":            "#3b82f6",
  "Placed":                  "#6366f1",
  "Pending payment":         "#f97316",
  "Clinic processing":       "#0ea5e9",
  "In Waiting Room":         "#8b5cf6",
  "Doctor is about to call": "#f59e0b",
  "Consult Ending":          "#d97706",
  "Completed":               "#16a34a",
  "Cancelled":               "#ef4444",
  "No Answer":               "#9ca3af",
  "Clinic to update":        "#78716c",
  "In Appt Now":             "#10b981",
  "Appt Paused":             "#f59e0b",
  "Appt Ended":              "#6b7280",
  "Escalated":               "#dc2626",
  "Missed Appt":             "#ef4444",
  "Document Received":       "#8b5cf6",
  "Script Received":         "#10b981",
  "HubMed Confirmed":        "#ec4899",
};

// Resolve display label from numeric Status (preferred) or fall back to StatusLabel string
function resolveLabel(row) {
  if (row?.Status != null) return getDocLinkConsultStatusLabel(row.Status);
  return row?.StatusLabel ?? "—";
}

function ConsultLogDrawer({ open, onClose, consult, chemistUUID, isManager }) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !consult) return;
    setLog(null);
    setError(null);
    setLoading(true);
    const path = isManager
      ? API_PATHS.MANAGER_CHEMIST_DOCLINK_CONSULT_LOG
      : API_PATHS.CHEMIST_DOCLINK_CONSULT_LOG;
    phappsRequest(path, "GET", null, { ChemistUUID: chemistUUID, ConsultID: consult.ConsultID })
      .then((data) => setLog(data))
      .catch(() => setError("Failed to load status history."))
      .finally(() => setLoading(false));
  }, [open, consult, chemistUUID, isManager]);

  const patientName = consult
    ? [consult.FirstName, consult.LastName].filter(Boolean).join(" ") || "Unknown patient"
    : "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: PURPLE }}>
        <Box>
          <Typography fontWeight={700} fontSize="0.95rem" color="#fff">
            Consult #{consult?.ConsultID}
          </Typography>
          <Typography fontSize="0.78rem" sx={{ color: "#d8c9f0", mt: 0.25 }}>
            {patientName} · {fmtDate(consult?.CreateDate)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Status badge row */}
      {consult && (
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={resolveLabel(consult)}
            size="small"
            sx={{
              backgroundColor: `${STATUS_COLORS[resolveLabel(consult)] ?? "#64748b"}22`,
              color: STATUS_COLORS[resolveLabel(consult)] ?? "#64748b",
              fontWeight: 700, fontSize: 11,
            }}
          />
          <Chip label={consult.Provider} size="small" sx={{ backgroundColor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: 11 }} />
          {consult.StaffName && (
            <Chip label={consult.StaffName} size="small" sx={{ backgroundColor: "#f1f5f9", color: "#475569", fontSize: 11 }} />
          )}
        </Box>
      )}

      {/* Timeline */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
        <Typography fontWeight={700} fontSize="0.78rem" sx={{ textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", mb: 1.5 }}>
          Status History
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: PURPLE }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {log && log.length === 0 && (
          <Typography variant="body2" color="text.secondary">No status changes recorded.</Typography>
        )}

        {log && log.length > 0 && (
          <Box sx={{ position: "relative" }}>
            {/* Vertical line */}
            <Box sx={{ position: "absolute", left: 9, top: 8, bottom: 8, width: 2, backgroundColor: "#e5e7eb" }} />

            {log.map((entry, i) => {
              const label = resolveLabel(entry);
              const color = STATUS_COLORS[label] ?? "#64748b";
              const isLast = i === log.length - 1;
              return (
                <Box key={entry.StatusLogID} sx={{ display: "flex", gap: 2, mb: isLast ? 0 : 2, position: "relative" }}>
                  {/* Dot */}
                  <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center", pt: 0.25 }}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: "50%",
                      backgroundColor: color,
                      border: "2px solid #fff",
                      boxShadow: `0 0 0 2px ${color}44`,
                      position: "relative", zIndex: 1,
                    }} />
                  </Box>
                  {/* Content */}
                  <Box sx={{ flex: 1, pb: isLast ? 0 : 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Chip
                        label={label}
                        size="small"
                        sx={{ backgroundColor: `${color}18`, color, fontWeight: 700, fontSize: 10, height: 20 }}
                      />
                    </Box>
                    <Typography fontSize="0.72rem" color="text.secondary" sx={{ mt: 0.25 }}>
                      {fmtLocalDateTime(entry.StatusChangeDate)}
                    </Typography>
                    {entry.Comment && (
                      <Typography fontSize="0.78rem" sx={{ mt: 0.5, color: "#374151", fontStyle: "italic" }}>
                        "{entry.Comment}"
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: PURPLE, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function RankList({ title, items, color }) {
  if (!items || items.length === 0) return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="textSecondary">No data in the last 30 days.</Typography>
    </Box>
  );
  const max = items[0]?.count ?? 1;
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 16, textAlign: "right" }}>{i + 1}.</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{item.name || "—"}</Typography>
                <Typography variant="body2" sx={{ color, fontWeight: 700, ml: 1 }}>{item.count}</Typography>
              </Box>
              <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(item.count / max) * 100}%`, backgroundColor: color, borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const PROVIDER_COLORS = {
  Medmate: "#2563eb",
  Hubmed: "#ec4899",
  Hola: "#16a34a",
  Other: "#64748b",
};

function buildApptColumns() {
  return [
    {
      field: "CreateDate",
      headerName: "Date",
      width: 90,
      renderCell: (p) => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
    },
    {
      field: "patient",
      headerName: "Patient",
      flex: 1,
      minWidth: 110,
      renderCell: (p) => (
        <Typography variant="body2" noWrap sx={{ width: 1 }}>
          {[p.row.FirstName, p.row.LastName].filter(Boolean).join(" ") || "—"}
        </Typography>
      ),
    },
    {
      field: "Provider",
      headerName: "Provider",
      width: 90,
      renderCell: (p) => {
        const color = PROVIDER_COLORS[p.value] ?? "#64748b";
        return <Chip label={p.value || "—"} size="small" sx={{ backgroundColor: `${color}22`, color, fontWeight: 700, fontSize: 11 }} />;
      },
    },
    {
      field: "StaffName",
      headerName: "Staff",
      width: 120,
      renderCell: (p) => (
        <Typography variant="body2" noWrap sx={{ width: 1 }}>
          {p.value || "—"}
        </Typography>
      ),
    },
    {
      field: "StatusLabel",
      headerName: "Status",
      width: 140,
      renderCell: (p) => {
        const label = resolveLabel(p.row);
        return (
          <Typography variant="body2" noWrap sx={{ width: 1, color: "text.secondary" }}>
            {label}
          </Typography>
        );
      },
    },
  ];
}

const ChemistDocLinkTab = ({ chemistUUID, isManager = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedConsult, setSelectedConsult] = useState(null);

  const handleOpenConsult = (row) => {
    setSelectedConsult(row);
    setDrawerOpen(true);
  };

  const fetchData = useCallback(async () => {
    if (!chemistUUID || data) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(isManager ? API_PATHS.MANAGER_CHEMIST_DOCLINK : API_PATHS.CHEMIST_DOCLINK, "GET", null, { ChemistUUID: chemistUUID });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load DocLink data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, data]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <Typography color="textSecondary">Loading DocLink data…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const { Metrics, ConsultsPerDay, ProviderBreakdown, TopStaffAppointments, RecentAppointments } = data;
  const waitLabel = Metrics?.AvgWaitTimeMinutes != null
    ? `${Number(Metrics.AvgWaitTimeMinutes).toFixed(0)} min`
    : "—";

  const apptRows = (RecentAppointments ?? []).map((r, i) => ({ id: r.ConsultID ?? i, ...r }));
  const apptColumns = buildApptColumns();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <ConsultLogDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        consult={selectedConsult}
        chemistUUID={chemistUUID}
        isManager={isManager}
      />
      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label="Total consults (30d)"
          value={Number(Metrics?.TotalConsults30d ?? 0).toLocaleString()}
        />
        <StatCard
          label="Script requests (30d)"
          value={Number(Metrics?.TotalScriptRequests30d ?? 0).toLocaleString()}
        />
        <StatCard
          label="Avg wait time"
          value={waitLabel}
          sub="time to action"
        />
        <StatCard
          label="Avg / Day"
          value={Number(Metrics?.AvgConsultsPerDay ?? 0).toFixed(1)}
          sub="consults"
        />
        <StatCard
          label="All-time consults"
          value={Number(Metrics?.TotalConsultsAllTime ?? 0).toLocaleString()}
        />
      </Box>

      {/* Main layout: LEFT (chart + provider/staff) | RIGHT (appointments same height) */}
      {/* Left total: chart(240) + gap(16) + bottom-row(200) = 456px                        */}
      {/* Appointments DataGrid: 456 - card padding(32) - title row(32) = 392px             */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>

        {/* LEFT: chart then provider + staff row */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Chart card */}
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE, mb: 1 }}>
              Consults per day (30d)
            </Typography>
            <Box sx={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ConsultsPerDay} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={fmtDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <ReTooltip
                    formatter={(v, name) => [v, name === "consults" ? "Consult" : "Script Request"]}
                    labelFormatter={(v) => `Date: ${fmtDate(v)}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                  />
                  <Legend
                    formatter={(v) => v === "consults" ? "Consult" : "Script Request"}
                    iconSize={10}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  <Bar dataKey="consults" stackId="a" fill={PURPLE} radius={[0, 0, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="scriptRequests" stackId="a" fill={ORANGE} radius={[2, 2, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* Provider breakdown (left) + Top staff (right) — 50/50, fixed height 200px each */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>

            {/* Provider breakdown */}
            <Box sx={{
              height: 200, p: 2, borderRadius: 2, border: "1px solid #e5e7eb",
              backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              display: "flex", flexDirection: "column",
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE, mb: 1.5 }}>
                Provider breakdown (30d)
              </Typography>
              <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexWrap: "wrap", gap: 1.5, alignContent: "flex-start" }}>
                {(ProviderBreakdown ?? []).slice(0, 3).map((p) => {
                  const color = PROVIDER_COLORS[p.name] || "#64748b";
                  const total = (ProviderBreakdown ?? []).reduce((s, r) => s + r.count, 0);
                  const pct = total ? Math.round((p.count / total) * 100) : 0;
                  return (
                    <Box
                      key={p.name}
                      sx={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        gap: 0.5, px: 1.5, py: 1, borderRadius: 2,
                        border: `2px solid ${color}`, minWidth: 72,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{p.count}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color }}>{p.name}</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{pct}%</Typography>
                    </Box>
                  );
                })}
                {(!ProviderBreakdown || ProviderBreakdown.length === 0) && (
                  <Typography variant="body2" color="textSecondary">No data.</Typography>
                )}
              </Box>
            </Box>

            {/* Top staff */}
            <Box sx={{
              height: 200, p: 2, borderRadius: 2, border: "1px solid #e5e7eb",
              backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              display: "flex", flexDirection: "column",
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE, mb: 1.5 }}>
                Top staff by appointments (30d)
              </Typography>
              {(TopStaffAppointments ?? []).length > 0 ? (
                <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {TopStaffAppointments.map((item, i) => {
                    const max = TopStaffAppointments[0]?.count ?? 1;
                    return (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 14, textAlign: "right" }}>{i + 1}.</Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: "0.78rem" }}>{item.name || "—"}</Typography>
                            <Typography variant="body2" sx={{ color: PURPLE, fontWeight: 700, ml: 1, fontSize: "0.78rem" }}>{item.count}</Typography>
                          </Box>
                          <Box sx={{ height: 3, borderRadius: 2, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                            <Box sx={{ height: "100%", width: `${(item.count / max) * 100}%`, backgroundColor: PURPLE, borderRadius: 2 }} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">No data.</Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* RIGHT: Recent appointments — fixed height matching left total (456px) */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE, mb: 1.5 }}>
            Recent appointments (last 30)
          </Typography>
          {apptRows.length > 0 ? (
            <Box sx={{ height: 392 }}>
              <DataGrid
                rows={apptRows}
                columns={apptColumns}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter
                onRowClick={(params) => handleOpenConsult(params.row)}
                sx={{
                  ...pharmacyHaloDataGridSx,
                  height: "100%",
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No appointments found.</Typography>
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default ChemistDocLinkTab;
